import {
    Button,
    Drawer,
    DrawerProps,
    Group,
    ScrollArea,
    Select,
    Stack,
} from '@mantine/core';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    SubscriptionsFilters,
    useSubscriptionsFilter,
} from '../hooks/useSubscriptionsFilter';
import {SubscriptionStatus} from '../types';

/**
 * FNP-918: the subscriptions filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (useSubscriptionsFilter), not component state, so the
 * table — a sibling, not a child — filters the same list.
 *
 * ⚠️ Only `status` is offered. It is the one enum field on the entity;
 * `customerId`, `description` and `currency` are free text already covered by
 * the keyword search, and a control for them would duplicate it. Adding
 * filters for fields the operator cannot meaningfully pick from would be
 * building for a hypothetical use case.
 *
 * ⚠️ Filtering is CLIENT-SIDE over the loaded window — the API's
 * SubscriptionsFilterInput is used only for `customerId`, which the customer
 * detail page passes as a prop rather than through this drawer. See
 * useSubscriptions.ts.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useSubscriptionsFilter();

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease',
            }}
            position='right'
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Subscriptions')}
            padding='xl'
            size='md'
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <Formik<SubscriptionsFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <Select
                                label={I18n.get('Status')}
                                placeholder={I18n.get('Any')}
                                clearable
                                // Driven off the enum so a new Stripe status
                                // reaches the drawer by adding it in one place.
                                data={Object.values(SubscriptionStatus)
                                            .map(status => ({
                                                value: status,
                                                label: I18n.get(status),
                                            }))}
                                value={values.status ?? null}
                                onChange={(value) =>
                                    setFieldValue('status', value ?? '')}
                            />

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Drawer>
    );
};
